import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomDiscountRow } from '../../models/discount.models';
import { formatDiscountAmount } from '../../utils/discount-display.util';

/**
 * Custom Discount table: orders that had a custom discount applied.
 * The Order ID opens that order on the Orders page.
 */
@Component({
  selector: 'app-custom-discount-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-discount-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./custom-discount-table.component.scss'],
})
export class CustomDiscountTableComponent {
  @Input() rows: CustomDiscountRow[] = [];
  @Input() sortDirection: 'asc' | 'desc' = 'desc';

  @Output() sortByCreatedDate = new EventEmitter<void>();
  @Output() viewOrder = new EventEmitter<CustomDiscountRow>();

  get sortIcon(): string {
    return this.sortDirection === 'asc'
      ? '/assets/images/time_sort_ascending.png'
      : '/assets/images/time_sort_descending.png';
  }

  /**
   * Order ids are stored with a leading '#' in some rows and without it in others,
   * so normalise to exactly one before rendering.
   */
  displayOrderId(row: CustomDiscountRow): string {
    const orderId = String(row.order_id ?? '').trim();
    if (!orderId) {
      return '-';
    }
    return orderId.startsWith('#') ? orderId : `#${orderId}`;
  }

  /** The API already formats this (20% vs $150); fall back locally if it is absent. */
  amount(row: CustomDiscountRow): string {
    return row.discount_display || formatDiscountAmount(row.discount_type, row.discount_value);
  }

  /** Saved attendant reason, or `-` when missing (legacy / non-attendant rows). */
  reason(row: CustomDiscountRow): string {
    const value = String(row.reason ?? '').trim();
    return value || '-';
  }

  trackByOrder(_index: number, row: CustomDiscountRow): string {
    return row.order_uuid;
  }
}
